/**
 * Funnel analytics tracker.
 * Fire-and-forget – never blocks UI.
 *
 * Google Ads integration:
 *   - Captures gclid from URL on page load and persists in sessionStorage
 *   - Fires Google Ads conversions defensively (only if env vars are set)
 *   - Three conversion events: email_submitted (primary), start_demo, demo_generated (secondary)
 */
import { API_URL } from "./api";
import { getConsent } from "@/utils/cookieConsent";

function statisticsAllowed() {
  try { return !!getConsent().statistics; } catch { return false; }
}
function marketingAllowed() {
  try { return !!getConsent().marketing; } catch { return false; }
}

// ============================================
// SESSION ID
// ============================================

let _sessionId = null;

function getSessionId() {
  // Funnel-analys är statistics-tier (Basic Consent Mode). Utan
  // statistics-samtycke skapas/läses INTE funnel_session_id.
  if (!statisticsAllowed()) return null;
  if (_sessionId) return _sessionId;
  try {
    _sessionId = sessionStorage.getItem("funnel_session_id");
    if (!_sessionId) {
      _sessionId = `fs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("funnel_session_id", _sessionId);
    }
  } catch {
    return null;
  }
  return _sessionId;
}

// ============================================
// GCLID CAPTURE
// ============================================

/**
 * gclid är separat marknadsattribution och får ENDAST fångas/läsas/sparas
 * med marketing-samtycke. Utan marketing: ingen gclid i sessionStorage och
 * ingen gclid i funnel-events (statistics=true + marketing=false = funnel
 * fungerar men helt utan gclid).
 */
function captureGclid() {
  if (typeof window === "undefined") return;
  if (!marketingAllowed()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid");
    if (gclid) {
      sessionStorage.setItem("gclid", gclid);
    }
  } catch (_) {}
}

export function getGclid() {
  if (typeof window === "undefined") return null;
  if (!marketingAllowed()) return null;
  try {
    return sessionStorage.getItem("gclid") || null;
  } catch {
    return null;
  }
}

// ============================================
// GOOGLE ADS CONVERSION
// ============================================

const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";

const CONVERSION_LABELS = {
  email_submitted: process.env.REACT_APP_GOOGLE_ADS_LABEL_EMAIL_SUBMITTED || "",
  start_demo: process.env.REACT_APP_GOOGLE_ADS_LABEL_START_DEMO || "",
  demo_generated: process.env.REACT_APP_GOOGLE_ADS_LABEL_DEMO_GENERATED || "",
};

/**
 * Wait for window.gtag to become available (loaded async by the main tag).
 * Resolves immediately if already present, otherwise polls up to `timeoutMs`.
 */
function waitForGtag(timeoutMs = 1500) {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (typeof window.gtag === "function") return resolve(window.gtag);
    const start = Date.now();
    const iv = setInterval(() => {
      if (typeof window.gtag === "function") {
        clearInterval(iv);
        resolve(window.gtag);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(iv);
        resolve(null);
      }
    }, 50);
  });
}

/**
 * Fire a Google Ads conversion event.
 * - Waits up to 1.5s for gtag to load (handles async loader race).
 * - Uses event_callback so the pixel is guaranteed to be sent before any redirect.
 * - Returns a Promise that resolves after the conversion is registered (or times out).
 * - Always logs when attempted, so Tag Assistant debugging works in preview + prod.
 */
export function fireGoogleConversion(conversionName) {
  return new Promise((resolve) => {
    try {
      // Google Ads-konvertering → kräver marketing-samtycke (Basic Consent
      // Mode). Utan marketing laddas gtag.js aldrig och inget skickas.
      if (!marketingAllowed()) {
        return resolve(false);
      }
      if (!GOOGLE_ADS_ID) {
        console.warn(`[Google Ads] Skipped '${conversionName}' – REACT_APP_GOOGLE_ADS_ID not set`);
        return resolve(false);
      }
      const label = CONVERSION_LABELS[conversionName];
      if (!label) {
        console.warn(`[Google Ads] Skipped '${conversionName}' – no conversion label configured`);
        return resolve(false);
      }

      const sendTo = `${GOOGLE_ADS_ID}/${label}`;

      waitForGtag(1500).then((gtag) => {
        if (!gtag) {
          console.warn(`[Google Ads] gtag not available for '${conversionName}' (send_to=${sendTo})`);
          return resolve(false);
        }

        let settled = false;
        const done = (reason) => {
          if (settled) return;
          settled = true;
          console.log(`[Google Ads] Conversion '${conversionName}' → ${sendTo} [${reason}]`);
          resolve(true);
        };

        // Hard safety net in case event_callback is never invoked
        // (e.g. ad blocker, network error). We still resolve so the UI doesn't hang.
        const fallbackTimer = setTimeout(() => done("timeout"), 1200);

        try {
          gtag("event", "conversion", {
            send_to: sendTo,
            event_callback: () => {
              clearTimeout(fallbackTimer);
              done("callback");
            },
          });
        } catch (err) {
          clearTimeout(fallbackTimer);
          console.warn(`[Google Ads] gtag threw for '${conversionName}':`, err);
          resolve(false);
        }
      });
    } catch (_) {
      // Never crash on tracking
      resolve(false);
    }
  });
}

// ============================================
// INTERNAL FUNNEL TRACKING
// ============================================

export function trackFunnel(eventType, source, metadata = {}) {
  if (!statisticsAllowed()) return; // funnel-analys = statistics-tier
  const sessionId = getSessionId();
  if (!sessionId) return;
  captureGclid();
  const pageUrl = typeof window !== "undefined" ? window.location.pathname : "";
  const gclid = getGclid(); // marketing-gated

  fetch(`${API_URL}/api/analytics/event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: sessionId,
      event_type: eventType,
      source,
      page_url: pageUrl,
      metadata: { ...metadata, ...(gclid ? { gclid } : {}) },
    }),
  }).catch(() => {});
}

/**
 * Use the chatbot's existing session_id for correlation.
 */
export function trackFunnelWithSession(sessionId, eventType, source, metadata = {}) {
  if (!statisticsAllowed()) return; // funnel-analys = statistics-tier
  captureGclid();
  const pageUrl = typeof window !== "undefined" ? window.location.pathname : "";
  const gclid = getGclid(); // marketing-gated

  fetch(`${API_URL}/api/analytics/event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: sessionId,
      event_type: eventType,
      source,
      page_url: pageUrl,
      metadata: { ...metadata, ...(gclid ? { gclid } : {}) },
    }),
  }).catch(() => {});
}
